import { AxiosResponse } from 'axios';
import { api } from 'src/common/utils';

import {
  AccountType,
  UserType,
  BusinessPhone,
  BillingSettingType,
  AccountProfileFeaturesType,
  TwilioConfigurationType,
  TwilioEndUserType,
  TwilioAddressType,
  IndustryType,
  ParentAccountType,
  LegalEntityType,
} from '../types';

import { SalesforceAccountType } from './types';

const ACCOUNTS_ROOT_PATH = 'account_admin';
const PARENT_ACCOUNTS_ROOT_PATH = 'parent_account_admin';
const LEGAL_ENTITIES_ROOT_PATH = 'legal_entities';

export type RequestApiPaginationResponseType<T> = {
  data: {
    count: number;
    next: string;
    previous: string;
    results: T[];
  };
};

type CreateBusinessPhonePayloadType = {
  account: string;
  number: string;
  digits_after_call: string;
};

type ScheduleTestCallsPayloadType = {
  account_id: string;
};

type CreateUserPayload = {
  account: string;
  first_name: string;
  last_name: string;
  username: string;
  is_active: boolean;
};

type UpdateBusinessPhonePayloadType = {
  is_enabled_for_call_rescue: boolean;
};

export const fetchAccount = async (_: string, accountId: string): Promise<AccountType> => {
  const response = await api.get(`/${ACCOUNTS_ROOT_PATH}/${accountId}`);
  return response.data as AccountType;
};

interface UpdateAccountType
  extends Omit<
    AccountType,
    'company_industry' | 'parent_account' | 'legal_entity' | 'campaign_detail'
  > {
  company_industry?: string;
  calendar_sync_enabled: boolean;
  confirmations_enabled: boolean;
  cancelations_enabled: boolean;
  is_waiting_list_enabled: boolean;
  sms_language_detection_enabled: boolean;
  scheduling_enabled: boolean;
  confirmations_should_include_time: boolean;
  has_active_subscription: boolean;
  text_back_enabled: boolean;
  voicemail_enabled: boolean;
  out_of_sync_scheduling_enabled: boolean;
  has_telemedicine: boolean;
  bot_number_area_code: string;
  parent_account: string | null;
  legal_entity: string | null;
  campaign_detail: string | null;
}

export const updateAccount = async (
  id: string,
  payload: Partial<UpdateAccountType>
): Promise<AxiosResponse<AccountProfileFeaturesType>> => {
  if (!id) throw new Error('Missing account id');
  return api.patch(`/${ACCOUNTS_ROOT_PATH}/${id}`, payload);
};

export const updateUser = (
  payload: Partial<UserType & { available_accounts_for_switch: string[] }>
): Promise<AxiosResponse> => {
  if (!payload.id) throw new Error('Missing user id');
  return api.patch(`/users/${payload.id}`, payload);
};

export const fetchIndustry = (): Promise<AxiosResponse<IndustryType[]>> => api.get('/industry');

export const createBusinessPhone = (
  payload: CreateBusinessPhonePayloadType
): Promise<AxiosResponse<BusinessPhone>> => api.post(`/business_phone`, payload);

export const updateBusinessPhone = (
  id: string,
  payload: UpdateBusinessPhonePayloadType
): Promise<AxiosResponse<BusinessPhone>> => api.patch(`/business_phone/${id}`, payload);

export const scheduleTestCalls = (payload: ScheduleTestCallsPayloadType): Promise<AxiosResponse> =>
  api.post(`/call_rescue_test_for_account/create_call_rescue_test`, payload);

export const createUser = async (payload: CreateUserPayload): Promise<AxiosResponse<UserType>> =>
  api.post(`/users`, payload);

export const fetchSalesforceAccounts = (
  name: string
): Promise<AxiosResponse<SalesforceAccountType[]>> =>
  api.get(`${ACCOUNTS_ROOT_PATH}/search?name=${name}`);

export const fetchParentAccounts = (name: string): Promise<AxiosResponse<ParentAccountType[]>> =>
  api.get(`${PARENT_ACCOUNTS_ROOT_PATH}?search=${name}`);

export const updateBillingSetting = (
  payload: BillingSettingType
): Promise<AxiosResponse<BillingSettingType>> =>
  api.patch(`/billing_setting/${payload.id}`, payload);

export const createTwilioConfiguration = async (
  account_id: string
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_subaccount`);
  return response.data as TwilioConfigurationType;
};

export const createTwilioStarterProfile = async (
  account_id: string
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_starter_profile`);
  return response.data as TwilioConfigurationType;
};

export const createTwilioEndUser = async (
  account_id: string,
  payload: TwilioEndUserType
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_end_user`, payload);
  return response.data as TwilioConfigurationType;
};

export const createTwilioAddress = async (
  account_id: string,
  payload: TwilioAddressType
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_address`, payload);
  return response.data as TwilioConfigurationType;
};

export const createTwilioA2PProfile = async (
  account_id: string
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_a2p_profile`);
  return response.data as TwilioConfigurationType;
};

export const createTwilioA2PBrand = async (
  account_id: string
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_a2p_brand`);
  return response.data as TwilioConfigurationType;
};

export const createTwilioA2PUseCase = async (
  account_id: string
): Promise<TwilioConfigurationType> => {
  const response = await api.post(`/twilio_admin/${account_id}/create_a2p_use_case`);
  return response.data as TwilioConfigurationType;
};

export const fetchLegalEntities = (
  name: string
): Promise<RequestApiPaginationResponseType<LegalEntityType>> =>
  api.get(`${LEGAL_ENTITIES_ROOT_PATH}?search=${name}`);
